import { useState } from 'react'
import { Check, ChevronsDownUp, ChevronsUpDown, Layers } from 'lucide-react'

import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover'
import { cn } from 'lib/utils'

import type { ExplorerColumn } from '../types/explorer.types'

interface GroupByControlProps {
	/** The SELECTED columns, in their display order — not the whole catalog. */
	columns: ExplorerColumn[]
	groupBy: string | null
	onChange: (key: string | null) => void
	/** Number of groups on the current page; drives the collapse-all label. */
	groupCount: number
	allCollapsed: boolean
	onToggleAll: () => void
}

/**
 * Choose the grouping column, and collapse or expand every group (MYD-369).
 *
 * ## Why only selected columns are offered
 *
 * The engine rejects a groupBy naming a column that is not in the report
 * ("Cannot group by X — add it as a column first"), because grouping is
 * applied as a leading sort over a column the row actually carries. Offering
 * the full catalog here would let a user pick something that comes straight
 * back as a 400 blaming them for a choice this control invited. So the list is
 * the selected, groupable columns, and when there are none the menu says which
 * control to reach for instead.
 *
 * Groupable is the server's own flag: it marks the low-cardinality columns.
 * Grouping by a free-text name would draw one header per row.
 */
export function GroupByControl({
	columns,
	groupBy,
	onChange,
	groupCount,
	allCollapsed,
	onToggleAll,
}: GroupByControlProps) {
	const [open, setOpen] = useState(false)

	const groupable = columns.filter(column => column.groupable)
	const active = columns.find(column => column.key === groupBy) ?? null

	const choose = (key: string | null) => {
		onChange(key)
		setOpen(false)
	}

	return (
		<div className="flex items-center gap-2">
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<button
						type="button"
						className={cn(
							'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs',
							active
								? 'border-primary/40 bg-primary/5 text-gray-900'
								: 'border-dashed border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-900',
						)}
					>
						<Layers className="w-3 h-3" />
						{active ? (
							<>
								Grouped by <span className="font-medium">{active.label}</span>
							</>
						) : (
							'Group by'
						)}
					</button>
				</PopoverTrigger>

				<PopoverContent align="start" className="w-56 p-1">
					{groupable.length === 0 ? (
						<p className="text-xs text-gray-400 px-2 py-2">
							None of your columns can be grouped. Add one — City or State, say — from
							Columns.
						</p>
					) : (
						<>
							<button
								type="button"
								onClick={() => choose(null)}
								className="w-full flex items-center px-2 py-1.5 rounded-md text-left text-sm text-gray-700 hover:bg-gray-50"
							>
								<Check
									className={cn(
										'w-4 h-4 mr-2 shrink-0',
										groupBy === null ? 'text-primary' : 'text-transparent',
									)}
								/>
								No grouping
							</button>

							{groupable.map(column => (
								<button
									key={column.key}
									type="button"
									onClick={() => choose(column.key)}
									className="w-full flex items-center px-2 py-1.5 rounded-md text-left text-sm text-gray-700 hover:bg-gray-50"
								>
									<Check
										className={cn(
											'w-4 h-4 mr-2 shrink-0',
											groupBy === column.key ? 'text-primary' : 'text-transparent',
										)}
									/>
									<span className="truncate">{column.label}</span>
								</button>
							))}
						</>
					)}
				</PopoverContent>
			</Popover>

			{/* Only worth offering once there is more than one group to act on: a
			    single group collapses to nothing but its own header. */}
			{active && groupCount > 1 && (
				<button
					type="button"
					onClick={onToggleAll}
					className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50 hover:text-gray-900"
				>
					{allCollapsed ? (
						<ChevronsUpDown className="w-3 h-3" />
					) : (
						<ChevronsDownUp className="w-3 h-3" />
					)}
					{allCollapsed ? 'Expand all' : 'Collapse all'}
				</button>
			)}
		</div>
	)
}

export default GroupByControl
